import apiClient from './apiClient';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut
} from 'firebase/auth';
import { auth } from './firebaseConfig';
import { firestoreService } from './firestoreService';

export interface User {
  id: string;
  username: string;
  email: string;
  avatar: string;
  provider: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  credits: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

class UserService {
  // 检查用户登录状态
  async checkAuthStatus(): Promise<boolean> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return false;
      }

      // 验证token有效性
      const response = await apiClient.get('/auth/verify');
      return response.status === 200;
    } catch (error) {
      console.error('检查认证状态失败:', error);
      return false;
    }
  }

  // 请求验证码
  async requestVerificationCode(email: string): Promise<{ verificationCode?: string }> {
    try {
      const response = await apiClient.post('/auth/request-code', { email });
      return response.data;
    } catch (error) {
      console.error('请求验证码失败:', error);
      throw error;
    }
  }

  // 用户注册（Firebase 认证）
  async register(email: string, password: string, username?: string): Promise<AuthResponse> {
    try {
      // 使用 Firebase 创建用户
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // 创建用户数据对象
      const user: User = {
        id: firebaseUser.uid,
        username: username || firebaseUser.displayName || 'User',
        email: firebaseUser.email || email,
        avatar: firebaseUser.photoURL || '/uploads/demo-avatar.png',
        provider: 'firebase',
        emailVerified: firebaseUser.emailVerified,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        credits: 5 // 新用户默认积分
      };
      
      // 保存用户数据到 Firestore
      try {
        await firestoreService.createOrUpdateUser(user);
        console.log('用户数据已保存到 Firestore');
      } catch (firestoreError) {
        console.warn('Firestore 用户创建失败，但 Firebase 用户已创建:', firestoreError);
      }
      
      // 🔥 发送邮箱验证邮件
      try {
        await sendEmailVerification(firebaseUser);
        console.log('✅ 邮箱验证邮件已发送');
      } catch (emailError) {
        console.warn('发送验证邮件失败，但用户已创建:', emailError);
      }
      
      // 保存用户信息到本地存储
      localStorage.setItem('userInfo', JSON.stringify(user));

      return { token: firebaseUser.uid, user };
    } catch (error: any) {
      console.error('注册失败:', error);
      
      // 处理 Firebase 错误
      let errorMessage = '注册失败，请重试';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = '该邮箱已被使用';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = '密码强度不够，请使用至少6位字符';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '邮箱格式不正确';
      }
      
      throw new Error(errorMessage);
    }
  }

  // 用户登录（Firebase 认证）
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      // 使用 Firebase 登录
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // 创建用户数据对象
      const user: User = {
        id: firebaseUser.uid,
        username: firebaseUser.displayName || 'User',
        email: firebaseUser.email || email,
        avatar: firebaseUser.photoURL || '/uploads/demo-avatar.png',
        provider: 'firebase',
        emailVerified: firebaseUser.emailVerified,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        credits: 5
      };
      
      // 从 Firestore 获取用户详细信息
      try {
        const firestoreUser = await firestoreService.getUser(firebaseUser.uid);
        if (firestoreUser) {
          // 合并 Firestore 数据
          user.credits = firestoreUser.credits;
          user.createdAt = firestoreUser.createdAt.toDate().toISOString();
          user.updatedAt = firestoreUser.updatedAt.toDate().toISOString();
          user.lastLoginAt = firestoreUser.lastLoginAt.toDate().toISOString();
        } else {
          // 如果 Firestore 中没有用户数据，创建新记录
          await firestoreService.createOrUpdateUser(user);
        }
      } catch (firestoreError) {
        console.warn('获取 Firestore 用户信息失败，使用 Firebase 用户信息:', firestoreError);
      }
      
      // 保存用户信息到本地存储
      localStorage.setItem('userInfo', JSON.stringify(user));
      
      return { token: firebaseUser.uid, user };
    } catch (error: any) {
      console.error('登录失败:', error);
      
      // 处理 Firebase 错误
      let errorMessage = '登录失败，请重试';
      if (error.code === 'auth/user-not-found') {
        errorMessage = '用户不存在';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = '密码错误';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '邮箱格式不正确';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = '账户已被禁用';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = '登录尝试次数过多，请稍后再试';
      }
      
      throw new Error(errorMessage);
    }
  }

  // Google登录
  async googleLogin(googleToken: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/google', { token: googleToken });
      const { token, user } = response.data;
      
      // 保存token和用户信息
      localStorage.setItem('authToken', token);
      localStorage.setItem('userInfo', JSON.stringify(user));
      
      return { token, user };
    } catch (error) {
      console.error('Google登录失败:', error);
      throw error;
    }
  }

  // 获取当前用户信息
  async getCurrentUser(): Promise<User | null> {
    try {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        return JSON.parse(userInfo);
      }
      return null;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return null;
    }
  }

  // 从 Firestore 获取用户详细信息
  async getUserProfile(): Promise<User> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('用户未登录');
      }

      const firestoreUser = await firestoreService.getUser(currentUser.uid);
      if (!firestoreUser) {
        throw new Error('用户数据不存在');
      }

      const user: User = {
        id: firestoreUser.id,
        username: firestoreUser.username,
        email: firestoreUser.email,
        avatar: firestoreUser.avatar,
        provider: firestoreUser.provider,
        emailVerified: firestoreUser.emailVerified,
        createdAt: firestoreUser.createdAt.toDate().toISOString(),
        updatedAt: firestoreUser.updatedAt.toDate().toISOString(),
        lastLoginAt: firestoreUser.lastLoginAt.toDate().toISOString(),
        credits: firestoreUser.credits
      };
      
      // 更新本地存储
      localStorage.setItem('userInfo', JSON.stringify(user));
      
      return user;
    } catch (error) {
      console.error('获取用户详细信息失败:', error);
      throw error;
    }
  }

  // 更新用户信息
  async updateUser(userData: Partial<User>): Promise<User> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('用户未登录');
      }

      // 更新 Firestore 中的用户数据
      await firestoreService.updateUser(currentUser.uid, userData);
      
      // 获取更新后的用户数据
      const updatedUser = await this.getUserProfile();
      
      return updatedUser;
    } catch (error) {
      console.error('更新用户信息失败:', error);
      throw error;
    }
  }

  // 修改密码
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await apiClient.put('/auth/password', {
        currentPassword,
        newPassword
      });
    } catch (error) {
      console.error('修改密码失败:', error);
      throw error;
    }
  }

  // 找回密码（Firebase 认证）
  async forgotPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      console.error('发送重置密码邮件失败:', error);
      
      // 处理 Firebase 错误
      let errorMessage = '发送重置密码邮件失败';
      if (error.code === 'auth/user-not-found') {
        errorMessage = '该邮箱未注册';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '邮箱格式不正确';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = '请求过于频繁，请稍后再试';
      }
      
      throw new Error(errorMessage);
    }
  }

  // 重新发送邮箱验证邮件
  async resendEmailVerification(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('用户未登录');
      }
      
      if (currentUser.emailVerified) {
        throw new Error('邮箱已经验证过了');
      }
      
      await sendEmailVerification(currentUser);
      console.log('✅ 验证邮件已重新发送');
    } catch (error: any) {
      let errorMessage = '发送验证邮件失败，请重试';
      if (error.code === 'auth/too-many-requests') {
        errorMessage = '发送过于频繁，请稍后再试';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = '用户不存在';
      }
      console.error('重新发送验证邮件失败:', error);
      throw new Error(errorMessage);
    }
  }

  // 用户登出
  async logout(): Promise<void> {
    try {
      // Firebase 登出
      await signOut(auth);
    } catch (error) {
      console.error('Firebase 登出失败:', error);
    } finally {
      // 清理本地存储
      localStorage.removeItem('authToken');
      localStorage.removeItem('userInfo');
    }
  }

  // 删除账户
  async deleteAccount(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('用户未登录');
      }

      // 删除 Firestore 中的用户数据
      await firestoreService.deleteUser(currentUser.uid);
      
      // 删除 Firebase 用户账户
      await currentUser.delete();
      
      // 登出
      this.logout();
    } catch (error) {
      console.error('删除账户失败:', error);
      throw error;
    }
  }

  // 获取用户统计信息
  async getUserStats(): Promise<any> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('用户未登录');
      }

      const stats = await firestoreService.getUserStats(currentUser.uid);
      return stats;
    } catch (error) {
      console.error('获取用户统计失败:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
export default userService;
